import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";

const LS_TOKEN = "bnm-admin-token";
const STATUSES = ["unread", "reviewed", "call_scheduled", "archived"];
const PROMPT_LABELS = {
  physical_holding: "The Physical Holding", unspoken_weight: "The Unspoken Weight",
  daily_cost: "The Daily Cost", space_of_unfolding: "The Space of Unfolding",
  next_gentle_step: "The Next Gentle Step",
};

export default function AdminReflections() {
  const [token] = useState(() => localStorage.getItem(LS_TOKEN) || "");
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [err, setErr] = useState("");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/admin/reflections`, auth); setRows(data.reflections || []); }
    catch (e) { setErr(e.response?.status === 401 ? "Please sign in at /admin first." : "Could not load reflections."); }
  }, [token]);
  useEffect(() => { if (token) load(); else setErr("Please sign in at /admin first."); }, [token, load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/reflections/${id}`, { status }, auth);
      setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
      if (sel?.id === id) setSel({ ...sel, status });
    } catch (e) {
      setErr(e.response?.status === 401 ? "Session expired — please sign in again at /admin." : "Could not update status. Please try again.");
    }
  };

  if (err) return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-ivory-100 px-5 text-center">
      <div><p className="text-ink-soft" data-testid="reflections-error">{err}</p><Link to="/admin" className="mt-3 inline-block text-gold-deep underline">Go to /admin</Link></div>
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="admin-reflections">
      <div className="mx-auto max-w-6xl px-5 pt-8 sm:px-8">
        <AdminNav />
        <h1 className="font-display text-3xl text-ink">A Quiet Inventory — Reflections</h1>
      </div>
      <div className="mx-auto grid max-w-6xl gap-6 px-5 py-8 sm:grid-cols-[320px_1fr] sm:px-8">
        <div className="space-y-2">
          {rows.length === 0 && <p className="text-sm text-ink-muted">No reflections yet.</p>}
          {rows.map((r) => (
            <button key={r.id} onClick={() => setSel(r)} data-testid={`reflection-row-${r.id}`}
              className={`block w-full rounded-xl p-3 text-left ring-1 ${sel?.id === r.id ? "bg-gold/10 ring-gold/40" : "bg-ivory-50 ring-ivory-400/50"}`}>
              <div className="flex items-center justify-between">
                <span className="font-display text-ink">{r.reader_name || (r.keep_private ? "Private" : "Anonymous")}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${r.status === "unread" ? "bg-terracotta/20 text-terracotta" : "bg-sage/25 text-ink-soft"}`}>{r.status}</span>
              </div>
              <p className="truncate text-xs text-ink-muted">{r.reader_email || "—"} · {new Date(r.created_at).toLocaleDateString()}{r.wants_followup ? " · wants call" : ""}</p>
            </button>
          ))}
        </div>

        <div>
          {!sel ? <p className="text-sm text-ink-muted">Select a reflection to read it.</p> : (
            <div className="rounded-2xl bg-ivory-50 p-6 ring-1 ring-ivory-400/50" data-testid="reflection-detail">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {STATUSES.map((s) => (
                  <button key={s} onClick={() => setStatus(sel.id, s)} data-testid={`set-status-${s}`}
                    className={`rounded-full px-3 py-1 text-xs ${sel.status === s ? "bg-ink text-ivory-50" : "border border-ink/20 text-ink-soft"}`}>{s.replace("_", " ")}</button>
                ))}
              </div>
              <p className="font-display text-lg text-ink">{sel.reader_name || "Anonymous"}{sel.mood ? ` · feeling ${sel.mood}` : ""}</p>
              <p className="text-sm text-ink-muted">{sel.reader_email || "—"}{sel.phone ? ` · ${sel.phone}` : ""}{sel.wants_followup ? " · requested a Braced Call" : ""}</p>
              <div className="mt-5 space-y-5">
                {Object.entries(sel.responses || {}).map(([k, v]) => (
                  <div key={k}>
                    <p className="kicker text-terracotta">{PROMPT_LABELS[k] || k}</p>
                    <p className="mt-1 whitespace-pre-wrap text-[15px] leading-relaxed text-ink">{v || "—"}</p>
                  </div>
                ))}
              </div>
              {sel.reader_email && !sel.keep_private && (
                <a href={`mailto:${sel.reader_email}`} className="debossed-btn mt-6 inline-flex rounded-full px-5 py-2.5 font-display text-ink">Reply personally</a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
